import { getFileActions, useFileStore } from "../stores/fileStore";
import { resolveAssetUrl, resolveCritterAssetsUrl } from "./assetPaths";
import {
  collectMjcfAssetRefsForFetch,
  registerMjcfFetchedAssetInFileMap,
} from "./mjcfAssetRefCollector";
import { applyMjcfCatalogTransforms } from "./mjcfCatalogTransforms";
import { logger } from "./logger";

const resolveRelativeUrl = (path, baseUrl) =>
  new URL(path, new URL(baseUrl, globalThis.location?.href || "http://localhost/")).toString();

const FETCH_RETRY_DELAYS_MS = [100, 300];

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithRetry = async (url, options = {}, { label = "asset" } = {}) => {
  let lastError = null;
  const maxAttempts = FETCH_RETRY_DELAYS_MS.length + 1;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await fetch(url, options);
      if (response.ok) return response;
      lastError = new Error(`HTTP ${response.status} for ${url}`);
    } catch (error) {
      lastError = error;
    }

    if (attempt < maxAttempts) {
      logger.warn("[xmlSourceLoader] Retrying MJCF fetch after failure", {
        label,
        url,
        attempt,
        maxAttempts,
        error: lastError instanceof Error ? lastError.message : String(lastError),
      });
      await delay(FETCH_RETRY_DELAYS_MS[attempt - 1]);
    }
  }
  throw lastError || new Error(`Failed to fetch ${label}: ${url}`);
};

const collectMjcfIncludeRefsForFetch = (xmlDoc, xmlUrl) =>
  Array.from(xmlDoc.querySelectorAll("include"))
    .map((includeEl) => {
      const includePath = includeEl.getAttribute("file")?.trim();
      if (!includePath) return null;
      const resolvedUrl = resolveRelativeUrl(includePath, xmlUrl);
      return {
        resolvedUrl,
        resolvedPath: new URL(resolvedUrl).pathname,
        aliases: [includePath],
      };
    })
    .filter(Boolean);

const fetchMjcfReferencesIntoFileMap = async (xmlDoc, xmlUrl, fileMap, fetchedUrls = new Set()) => {
  const assetRefs = collectMjcfAssetRefsForFetch(xmlDoc, xmlUrl);
  const includeRefs = collectMjcfIncludeRefsForFetch(xmlDoc, xmlUrl);
  const refs = [...assetRefs, ...includeRefs].filter(({ resolvedUrl }) => {
    if (fetchedUrls.has(resolvedUrl)) return false;
    fetchedUrls.add(resolvedUrl);
    return true;
  });

  const fetchedRefs = await Promise.allSettled(
    refs.map(async ({ resolvedUrl, resolvedPath, aliases }) => {
      const response = await fetchWithRetry(
        resolvedUrl,
        { cache: "no-store" },
        { label: "scene asset" }
      );
      const blob = await response.blob();
      return { resolvedUrl, resolvedPath, aliases, blob };
    })
  );

  const includeXmls = [];
  fetchedRefs.forEach((result) => {
    if (result.status === "rejected") {
      logger.warn("[xmlSourceLoader] Failed to fetch scene asset:", result.reason);
      return;
    }
    const { blob, resolvedUrl, resolvedPath, aliases } = result.value;
    registerMjcfFetchedAssetInFileMap(fileMap, { blob, resolvedUrl, resolvedPath, aliases });
    const isXml =
      resolvedPath.toLowerCase().endsWith(".xml") ||
      aliases.some((alias) =>
        String(alias || "")
          .toLowerCase()
          .endsWith(".xml")
      );
    if (isXml) includeXmls.push({ blob, resolvedUrl });
  });

  for (const { blob, resolvedUrl } of includeXmls) {
    const includeText = await blob.text();
    const includeDoc = new DOMParser().parseFromString(includeText, "text/xml");
    await fetchMjcfReferencesIntoFileMap(includeDoc, resolvedUrl, fileMap, fetchedUrls);
  }
};

export const buildXmlFileMap = async ({
  xmlPath,
  xmlFilename,
  baseFileMap = {},
  source,
  catalogEntry,
}) => {
  const xmlUrl =
    source === "critter-assets" ? resolveCritterAssetsUrl(xmlPath) : resolveAssetUrl(xmlPath);
  const xmlResponse = await fetchWithRetry(xmlUrl, { cache: "no-store" }, { label: "scene XML" });

  const xmlBlob = await xmlResponse.blob();
  const rawXmlText = await xmlBlob.text();
  const xmlText = applyMjcfCatalogTransforms(rawXmlText, catalogEntry);
  const xmlFile = new File([xmlText], xmlFilename, { type: xmlBlob.type || "application/xml" });
  const xmlDoc = new DOMParser().parseFromString(xmlText, "text/xml");

  const fileMap = {
    ...baseFileMap,
    [xmlFile.name]: xmlFile,
  };

  await fetchMjcfReferencesIntoFileMap(xmlDoc, xmlUrl, fileMap);

  return { xmlFile, fileMap };
};

export const loadXmlSourceScene = async ({
  xmlPath,
  xmlFilename,
  worldBodyNameBase,
  source,
  catalogEntry,
}) => {
  const { xmlFile, fileMap } = await buildXmlFileMap({
    xmlPath,
    xmlFilename,
    baseFileMap: {},
    source,
    catalogEntry,
  });
  const meshLibraryPathPrefix = String(xmlPath || "").replace(/\/[^/]*$/, "/");
  getFileActions().setFileMap(fileMap);
  await getFileActions().parseXmlFile(xmlFile, fileMap, {
    worldBodyNameBase,
    meshLibraryPathPrefix,
  });
  useFileStore.setState({
    currentLoadingFolder: undefined,
  });
  return { xmlFile, fileMap };
};

export const loadXmlCatalogEntry = async (entry) => {
  if (!entry?.xmlPath || !entry?.xmlFilename) {
    throw new Error("loadXmlCatalogEntry requires xmlPath and xmlFilename.");
  }
  return loadXmlSourceScene({
    xmlPath: entry.xmlPath,
    xmlFilename: entry.xmlFilename,
    worldBodyNameBase: entry.name,
    source: entry.source,
    catalogEntry: entry,
  });
};
